<script setup lang="ts">
/**
 * SecuritySection — board-wide security + AI policy.
 *
 * Security toggles (2FA, session, roster lock, IP allowlist) require President role.
 * Non-Presidents see locked toggles (padlock icon + tooltip).
 * AI toggles require BoardAdmin — shown to everyone but enforced at API level.
 *
 * D-S4: Roster lock copy: "Locks all member changes including roles and deactivation."
 */
import { ref, computed } from 'vue';
import BaseCard from '@/components/ui/BaseCard.vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import { useSettingsStore } from '@/stores/settings';
import { useAuth } from '@/stores/auth';

const store = useSettingsStore();
const auth = useAuth();

const isPresident = computed(() => auth.user?.role === 'Board.President');

// Local copies for AI policy (two-way in case user edits multiple toggles)
const localAiEnabled = computed(() => store.aiPolicy?.aiEnabled ?? false);
const localClassifierEnabled = computed(() => store.aiPolicy?.classifierEnabled ?? false);
const localSummaryEnabled = computed(() => store.aiPolicy?.summaryEnabled ?? false);

const ipAllowlistText = ref(store.boardPolicy?.ipAllowlistJson ?? '');

const savingSection = ref<string | null>(null);
const sectionError = ref<string | null>(null);

async function onSecurityToggle(
  field: 'requireTwoFactor' | 'rosterLocked' | 'ipAllowlistEnabled',
  value: boolean,
) {
  if (!store.boardPolicy) return;
  sectionError.value = null;
  savingSection.value = field;
  try {
    await store.saveBoardPolicy({
      ...store.boardPolicy,
      ipAllowlistJson: store.boardPolicy.ipAllowlistJson ?? null,
      [field]: value,
    });
  } catch (e) {
    sectionError.value = e instanceof Error ? e.message : 'Failed to save security policy.';
  } finally {
    savingSection.value = null;
  }
}

async function saveIpAllowlist() {
  if (!store.boardPolicy) return;
  sectionError.value = null;
  savingSection.value = 'ipAllowlist';
  try {
    await store.saveBoardPolicy({
      ...store.boardPolicy,
      ipAllowlistJson: ipAllowlistText.value || null,
    });
  } catch (e) {
    sectionError.value = e instanceof Error ? e.message : 'Failed to save IP allowlist.';
  } finally {
    savingSection.value = null;
  }
}

async function onAiToggle(
  field: 'aiEnabled' | 'aiClassifierEnabled' | 'aiSummaryEnabled',
  value: boolean,
) {
  if (!store.aiPolicy) return;
  sectionError.value = null;
  savingSection.value = field;
  try {
    await store.saveAiPolicy({
      aiEnabled: field === 'aiEnabled' ? value : localAiEnabled.value,
      aiClassifierEnabled: field === 'aiClassifierEnabled' ? value : localClassifierEnabled.value,
      aiSummaryEnabled: field === 'aiSummaryEnabled' ? value : localSummaryEnabled.value,
    });
  } catch (e) {
    sectionError.value = e instanceof Error ? e.message : 'Failed to save AI policy.';
  } finally {
    savingSection.value = null;
  }
}
</script>

<template>
  <BaseCard id="security" padding="none" data-testid="security-section">
    <div class="p-4 sm:p-6 border-b border-border">
      <h3 class="text-base font-semibold text-fg">Security</h3>
      <p class="text-sm text-muted mt-0.5">Workspace-wide security policies and AI controls.</p>
    </div>

    <div class="p-4 sm:p-6 space-y-0">
      <p
        v-if="!isPresident"
        class="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-3 py-2 mb-4"
      >
        Security policy changes are restricted to the President.
      </p>

      <!-- Require 2FA -->
      <div class="flex items-start justify-between gap-4 py-3 border-b border-border">
        <div class="grow min-w-0">
          <div class="text-sm font-medium text-fg flex items-center gap-1.5">
            Require two-factor authentication
            <svg
              v-if="!isPresident"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="w-3.5 h-3.5 text-muted"
              aria-label="President only"
            >
              <rect x="4" y="11" width="16" height="10" rx="2" />
              <path d="M8 11V7a4 4 0 1 1 8 0v4" />
            </svg>
          </div>
          <div class="text-xs text-muted mt-0.5">SMS or authenticator app required on sign-in for all board members.</div>
        </div>
        <button
          role="switch"
          :aria-checked="store.boardPolicy?.requireTwoFactor ?? false"
          :disabled="!isPresident || savingSection !== null"
          data-testid="security-toggle-2fa"
          class="relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-600"
          :class="[
            store.boardPolicy?.requireTwoFactor ? 'bg-navy-800' : 'bg-gray-300',
            !isPresident ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
          ]"
          @click="isPresident && onSecurityToggle('requireTwoFactor', !(store.boardPolicy?.requireTwoFactor ?? false))"
        >
          <span
            class="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform"
            :class="store.boardPolicy?.requireTwoFactor ? 'translate-x-4' : 'translate-x-0'"
          />
        </button>
      </div>

      <!-- Roster lock (D-S4) -->
      <div class="flex items-start justify-between gap-4 py-3 border-b border-border">
        <div class="grow min-w-0">
          <div class="text-sm font-medium text-fg flex items-center gap-1.5">
            Roster lock
            <svg
              v-if="!isPresident"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="w-3.5 h-3.5 text-muted"
              aria-label="President only"
            >
              <rect x="4" y="11" width="16" height="10" rx="2" />
              <path d="M8 11V7a4 4 0 1 1 8 0v4" />
            </svg>
          </div>
          <div class="text-xs text-muted mt-0.5">Locks all member changes including roles and deactivation.</div>
        </div>
        <button
          role="switch"
          :aria-checked="store.boardPolicy?.rosterLocked ?? false"
          :disabled="!isPresident || savingSection !== null"
          data-testid="security-toggle-roster"
          class="relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-600"
          :class="[
            store.boardPolicy?.rosterLocked ? 'bg-navy-800' : 'bg-gray-300',
            !isPresident ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
          ]"
          @click="isPresident && onSecurityToggle('rosterLocked', !(store.boardPolicy?.rosterLocked ?? false))"
        >
          <span
            class="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform"
            :class="store.boardPolicy?.rosterLocked ? 'translate-x-4' : 'translate-x-0'"
          />
        </button>
      </div>

      <!-- IP Allowlist -->
      <div class="py-3 border-b border-border space-y-3">
        <div class="flex items-start justify-between gap-4">
          <div class="grow min-w-0">
            <div class="text-sm font-medium text-fg flex items-center gap-1.5">
              IP allowlist
              <svg
                v-if="!isPresident"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                class="w-3.5 h-3.5 text-muted"
                aria-label="President only"
              >
                <rect x="4" y="11" width="16" height="10" rx="2" />
                <path d="M8 11V7a4 4 0 1 1 8 0v4" />
              </svg>
            </div>
            <div class="text-xs text-muted mt-0.5">Restrict workspace access to specific IPs (advanced).</div>
          </div>
          <button
            role="switch"
            :aria-checked="store.boardPolicy?.ipAllowlistEnabled ?? false"
            :disabled="!isPresident || savingSection !== null"
            data-testid="security-toggle-ip"
            class="relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-600"
            :class="[
              store.boardPolicy?.ipAllowlistEnabled ? 'bg-navy-800' : 'bg-gray-300',
              !isPresident ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
            ]"
            @click="isPresident && onSecurityToggle('ipAllowlistEnabled', !(store.boardPolicy?.ipAllowlistEnabled ?? false))"
          >
            <span
              class="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform"
              :class="store.boardPolicy?.ipAllowlistEnabled ? 'translate-x-4' : 'translate-x-0'"
            />
          </button>
        </div>

        <!-- CIDR textarea shown when IP allowlist is enabled -->
        <div v-if="store.boardPolicy?.ipAllowlistEnabled" class="space-y-2">
          <label class="text-xs text-muted font-medium" for="ip-allowlist-textarea">
            Allowed CIDRs (one per line)
          </label>
          <textarea
            id="ip-allowlist-textarea"
            v-model="ipAllowlistText"
            :disabled="!isPresident"
            rows="4"
            placeholder="192.168.1.0/24&#10;10.0.0.1/32"
            class="w-full px-3 py-2 rounded-md border border-border bg-surface text-sm font-mono text-fg resize-y focus:outline-none focus:ring-2 focus:ring-navy-600 disabled:opacity-50"
            data-testid="ip-allowlist-textarea"
          />
          <BaseButton
            v-if="isPresident"
            variant="secondary"
            size="sm"
            :loading="savingSection === 'ipAllowlist'"
            @click="saveIpAllowlist"
          >
            Save allowlist
          </BaseButton>
        </div>
      </div>

      <!-- AI policy (BoardAdmin required) -->
      <div class="pt-4">
        <h4 class="text-sm font-semibold text-fg mb-3">AI features</h4>

        <div class="space-y-0">
          <div class="flex items-start justify-between gap-4 py-3 border-b border-border">
            <div class="grow min-w-0">
              <div class="text-sm font-medium text-fg">AI enabled (master switch)</div>
              <div class="text-xs text-muted mt-0.5">Enables AI classification and dashboard summaries for this HOA.</div>
            </div>
            <button
              role="switch"
              :aria-checked="localAiEnabled"
              :disabled="savingSection !== null"
              data-testid="security-toggle-ai"
              class="relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
              :class="localAiEnabled ? 'bg-teal-600' : 'bg-gray-300'"
              @click="onAiToggle('aiEnabled', !localAiEnabled)"
            >
              <span
                class="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform"
                :class="localAiEnabled ? 'translate-x-4' : 'translate-x-0'"
              />
            </button>
          </div>

          <div
            class="flex items-start justify-between gap-4 py-3 border-b border-border transition-opacity"
            :class="!localAiEnabled ? 'opacity-40' : ''"
          >
            <div class="grow min-w-0">
              <div class="text-sm font-medium text-fg">Auto-classify severity</div>
              <div class="text-xs text-muted mt-0.5">AI automatically classifies issue severity on ingest.</div>
            </div>
            <button
              role="switch"
              :aria-checked="localClassifierEnabled"
              :disabled="!localAiEnabled || savingSection !== null"
              data-testid="security-toggle-classifier"
              class="relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
              :class="[
                localClassifierEnabled && localAiEnabled ? 'bg-teal-600' : 'bg-gray-300',
                !localAiEnabled ? 'cursor-not-allowed' : 'cursor-pointer',
              ]"
              @click="localAiEnabled && onAiToggle('aiClassifierEnabled', !localClassifierEnabled)"
            >
              <span
                class="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform"
                :class="localClassifierEnabled && localAiEnabled ? 'translate-x-4' : 'translate-x-0'"
              />
            </button>
          </div>

          <div
            class="flex items-start justify-between gap-4 py-3 transition-opacity"
            :class="!localAiEnabled ? 'opacity-40' : ''"
          >
            <div class="grow min-w-0">
              <div class="text-sm font-medium text-fg">Dashboard AI summary</div>
              <div class="text-xs text-muted mt-0.5">Show AI-generated issue summary card on the dashboard.</div>
            </div>
            <button
              role="switch"
              :aria-checked="localSummaryEnabled"
              :disabled="!localAiEnabled || savingSection !== null"
              data-testid="security-toggle-summary"
              class="relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
              :class="[
                localSummaryEnabled && localAiEnabled ? 'bg-teal-600' : 'bg-gray-300',
                !localAiEnabled ? 'cursor-not-allowed' : 'cursor-pointer',
              ]"
              @click="localAiEnabled && onAiToggle('aiSummaryEnabled', !localSummaryEnabled)"
            >
              <span
                class="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform"
                :class="localSummaryEnabled && localAiEnabled ? 'translate-x-4' : 'translate-x-0'"
              />
            </button>
          </div>
        </div>
      </div>

      <p v-if="sectionError" class="text-sm text-red-600 mt-3" role="alert">{{ sectionError }}</p>
    </div>
  </BaseCard>
</template>
